/**
 * equip-extras.ts
 *
 * EQuIP-specific types that are NOT synced from Keystone Platform.
 * update.sh only syncs equip-types.ts, equip-utils.ts, equip-measure-config.ts,
 * equip-measure-definitions.ts from lib/ — this file is safe from overwrites.
 *
 * MAINT-01: Roster profile artifacts live here to avoid regression via update.sh.
 */

import type { Role, EmploymentStatus } from '@/data/types'

/**
 * RosterProfile
 *
 * Shape returned by GET /api/equip/me/roster-profile.
 * Derived from employee roster (is_physician, bonus_eligible_type, employment_status)
 * joined with equip_eligibility_history for the current quarter.
 *
 * roleId is null when the user's roster attributes do not map to a known framework role.
 */
export interface RosterProfile {
  roleId: Role | null
  employmentStatus: EmploymentStatus | null
  isPhysician: boolean | null
  discipline: string | null
  firstName?: string | null
  lastName?: string | null
}

/** Preset roster profiles for framework persona testing (admin-only) */
export const PERSONA_OPTIONS: { label: string; profile: RosterProfile | null }[] = [
  { label: 'No persona (leadership view)', profile: null },
  {
    label: 'Primary Care Physician',
    profile: { roleId: 'primary-care-physician', isPhysician: true, discipline: 'Primary Care', employmentStatus: 'full-time' },
  },
  {
    label: 'Psychiatrist',
    profile: { roleId: 'psychiatry-physician', isPhysician: true, discipline: 'Psychiatry', employmentStatus: 'full-time' },
  },
  {
    label: 'Primary Care APC',
    profile: { roleId: 'primary-care-apc', isPhysician: false, discipline: 'Primary Care', employmentStatus: 'full-time' },
  },
  {
    label: 'Psychiatry APC',
    profile: { roleId: 'psychiatry-apc', isPhysician: false, discipline: 'Psychiatry', employmentStatus: 'full-time' },
  },
  {
    label: 'Palliative Care APC',
    profile: { roleId: 'palliative-care-apc', isPhysician: false, discipline: 'Palliative Care', employmentStatus: 'full-time' },
  },
  {
    label: 'Teletriage',
    profile: { roleId: 'teletriage', isPhysician: false, discipline: 'Teletriage', employmentStatus: 'full-time' },
  },
  {
    label: 'No roster match (fallback)',
    profile: { roleId: null, isPhysician: null, discipline: null, employmentStatus: null },
  },
]
